/**
 * buildAnimeTimelines()
 * -----------------
 * `buildAnimeTimelines` translates the relevant pieces
 * of an Ad's configuration into Anime timelines.
 */
import { __assign, __rest, __spreadArrays } from "tslib";
import * as animejs from 'animejs';
import { values } from 'shared/utils/misc';
import { buildTransition, getAnimeRefId, offsetTransforms, } from 'shared/anime/animeUtils';
import { defaultStyles } from 'shared/config';
import { isParent } from 'shared/symbols/symbolUtils';
import { buildConditionalStateId, getAnimationFromTimeline, } from 'shared/utils';
/**
 * Build AnimeJS for all the children of a Symbol.
 */
export var buildAnimeTimelinesForChildren = function (ad, layoutId, parentSymbolId) {
    var symbol = ad.symbols[parentSymbolId];
    return symbol
        ? buildAnimeTimelinesForSymbols(ad, layoutId, symbol.children || [])
        : [];
};
/**
 * Recursively build Animejs for an array of Symbols and their children.
 *
 * @param ignoreMissingConditionalStates Only used when calculating the duration of a parent container.
 *                                       Otherwise, Symbols without a ConditionalState will be assumed
 *                                       be non-visble and, thus, not needing to be animated.
 */
export var buildAnimeTimelinesForSymbols = function (ad, layoutId, symbolIds, parentOffset, ignoreMissingConditionalStates) {
    if (parentOffset === void 0) { parentOffset = 0; }
    if (ignoreMissingConditionalStates === void 0) { ignoreMissingConditionalStates = false; }
    return symbolIds.reduce(function (carrier, symbolId) {
        return carrier.concat(buildAnimeTimelineForSymbol(ad, layoutId, symbolId, parentOffset, ignoreMissingConditionalStates));
    }, []);
};
/**
 * Recursively build Animejs for a single Symbol and its children.
 *
 * @param ignoreMissingConditionalStates Only used when calculating the duration of a parent container.
 *                                       Otherwise, Symbols without a ConditionalState will be assumed
 *                                       be non-visble and, thus, not needing to be animated.
 */
export var buildAnimeTimelineForSymbol = function (ad, layoutId, symbolId, parentOffset, ignoreMissingConditionalStates) {
    if (parentOffset === void 0) { parentOffset = 0; }
    if (ignoreMissingConditionalStates === void 0) { ignoreMissingConditionalStates = false; }
    var symbol = ad.symbols[symbolId];
    var conditionalStateId = buildConditionalStateId(symbolId, layoutId);
    var conditionalState = values(ad.conditionalStates).find(function (c) { return c.id === conditionalStateId; });
    // In the final deliverable, non-visible Conditional States are stripped
    // out and do not need their timelines calculated. However, in the app,
    // we still need to calculate timelines for non-visible Conditional States
    // (`visible: false`) in order to have an accurate visual timeline.
    if (
    // Symbol not found. Shouldn't actually happen. This is a safe guard.
    !symbol ||
        // Conditional State was stripped out during Campaign Publish.
        (symbol.type !== 'stage' &&
            !ignoreMissingConditionalStates &&
            !conditionalState)) {
        return [];
    }
    var timeline = ad.timelines[symbolId];
    if (
    // Parent Symbols are always processed in order to determine
    // their length based on their children's lengths.
    !isParent(symbol) &&
        // Do not process Symbols without timeline.
        (!timeline ||
            // Do not process Symbols whose only animation is a hover.
            timeline.animations.filter(function (a) { return a.name !== 'hover'; }).length === 0)) {
        return [];
    }
    var duration = calculateGroupDuration(ad, symbol) ||
        (timeline ? timeline.duration : 0);
    var delay = (timeline && timeline.delay) || 0;
    var containerTimeline = __assign(__assign({}, timeline), { duration: duration,
        delay: delay });
    var symbolTimelines = buildAnimeTimelinesForConditionalState("#" + getAnimeRefId(symbolId), containerTimeline, conditionalState, parentOffset);
    var childrenTimelines = buildAnimeTimelinesForSymbols(ad, layoutId, symbol.children, calculateOffset(containerTimeline, parentOffset));
    var timelineParams = __spreadArrays(symbolTimelines, childrenTimelines);
    // IMPORTANT: Filter out any (offset:0 AND duration:0) params to ensure no buggy calculation in Animejs.
    return timelineParams.filter(function (p) { return !(p.duration === 0 && p.offset === 0); });
};
var buildAnimeTimelinesForConditionalState = function (targets, timeline, conditionalState, parentOffset) {
    if (parentOffset === void 0) { parentOffset = 0; }
    var restStyles = getAnimationStylesFromConditionalState(conditionalState);
    // Build the `in` transition.
    var aIn = getAnimationFromTimeline(timeline, 'in');
    var tIn = aIn &&
        buildTransition(targets, offsetTransforms(aIn.styles, restStyles), restStyles, parentOffset + timeline.delay, aIn.duration, aIn.easing);
    var rest = buildTransition(targets, restStyles, restStyles, parentOffset + timeline.delay + (aIn ? aIn.duration : 0), timeline.duration);
    // Build the `out` transition.
    var aOut = getAnimationFromTimeline(timeline, 'out');
    var tOut = aOut &&
        buildTransition(targets, restStyles, offsetTransforms(aOut.styles, restStyles), parentOffset +
            timeline.delay +
            (aIn ? aIn.duration : 0) +
            timeline.duration, aOut.duration, aOut.easing);
    // Filter out undefined
    return [tIn, rest, tOut].filter(isValidAnimation);
};
var isValidAnimation = function (animation) { return !!animation; };
/**
 * calculate offset to the nested timeline
 * it should includes the delay AND animation in duration
 * so the nested timeline animation only starts AFTER its animation in is completed
 */
export var calculateOffset = function (timeline, parentOffset) {
    // no offset on the focused symbol
    if (!timeline) {
        return parentOffset || 0;
    }
    var animationIn = getAnimationFromTimeline(timeline, 'in');
    // ensure there is animationIn & non-negative
    // fallback to duration of 0
    var animationInDuration = animationIn && animationIn.duration > 0 ? animationIn.duration : 0;
    return parentOffset + timeline.delay + animationInDuration;
};
/**
 * Extract animation-able styles from conditional state
 * Including styles from  'position' and 'styles'
 */
var getAnimationStylesFromConditionalState = function (conditionalState) {
    if (!conditionalState) {
        return {};
    }
    // We are omitting `fontSize`, because it may not be properly scaled
    // to the simulated view port. (vw's won't be accurate). If we animate
    // `fontSize` in the future, a better solution will be necessary.
    var _a = conditionalState.styles, fontSize = _a.fontSize, styles = __rest(_a, ["fontSize"]);
    var _b = (conditionalState.position || {}).rotate, rotate = _b === void 0 ? '0deg' : _b;
    return __assign(__assign(__assign({}, defaultStyles), { rotate: rotate }), styles);
};
/**
 * Calculate Group or Scene type duration depends on its children's duration.
 * Employ animejs timeline to accurately return the duration
 */
export var calculateGroupDuration = function (ad, symbol) {
    if (!isParent(symbol)) {
        return 0;
    }
    var layoutId = values(ad.conditionalStates)[0].layout;
    var groupTimelines = buildAnimeTimelinesForSymbols(ad, layoutId, symbol.children, 0, true);
    var groupAnimeTimeline = animejs.timeline({ autoplay: false });
    groupTimelines.forEach(function (tl) { return groupAnimeTimeline.add(tl); });
    return groupAnimeTimeline.duration;
};
